import React, { useState, useEffect, useCallback, useRef } from 'react';
import { windowService, documentService, sessionStorageService } from '../../../../utils/window.utils.js';
import DealerLocatorSearch from './components/dealerLocatorSearch';
import { TCountry, TDealershipSuggestion, TCardProps, onDealerResultsProps, TGooglePlaceSuggestion } from '../../types/dealerLocator.types.js';
import { Loader } from "@googlemaps/js-api-loader"
import Icon from '../../../@shared/icon';
import { iconSize } from '../../../../constants/icon.constants';
import DictionaryTerm from '../../../@shared/dictionaryTerm';
import CardCarousel from './components/cardCarousel';
import { fetchDealersByGeoCode, sortDealersByDistance } from './dealerLocator.utils';
import { APIPrefix } from '../../../../constants/api.constants';
import useSitecoreContext from '../../../../hooks/useSitecoreContext';
import { DEALER_LOCATOR_FORM_SEARCH } from '../../../../constants/dealerLocatorForm.constants';
import './styles.scss';

const DealerLocator = (props) => {
    const MAX_RESULTS = 6;
    const label = props?.field.model.title;
    const id = props.field.valueField.id;
    const mapsApiKey = props?.field?.model?.googleApikey ?? '';
    const requiredField = props?.field?.model?.required;
    const [country, setCountry] = useState<TCountry | undefined>(undefined);
    const [coords, setCoords] = useState({latitude: '', longitude: ''});
    const [distanceUnit, updateDistanceUnit] = useState(''); // eslint-disable-line
    const [countryList, setCountryList] = useState<TCountry[]>([]);
    const [searchInputValue, setSearchInputValue] = useState('');
    const {sitecoreContext} = useSitecoreContext();
    const itemID = sitecoreContext?.route?.itemId;
    const language = sitecoreContext.language;
    const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService>();
    const [placesService, setPlacesService] = useState<google.maps.places.PlacesService>();
    const [dealers, setDealers] = useState<TDealershipSuggestion[]>([]);
    const [dealerResultsLoaded, updateDealerResultsLoaded] = useState<boolean>(false); // eslint-disable-line
    const [cardProps, updateCardProps] = useState<TCardProps[]>([]); // eslint-disable-line
    const [showError, setShowError] = useState<boolean>(false);
    const controllerRef = useRef<AbortController | null>();
    const dealerDivRef = useRef<HTMLDivElement | null>(null);
    const FieldValidationErrors = props.fieldValidationErrorsComponent;
    const [userLocationIsActive, setUserLocationIsActive] = useState<boolean>(false);
    const formBgDesktopImage = sitecoreContext?.defaultBackgroundImageParameters?.desktopImageData?.formsDealerLocatorBackground;
    const formBgMobileImage = sitecoreContext?.defaultBackgroundImageParameters?.mobileImageData?.formsDealerLocatorBackground;
    const [searchUserLocationResult, setSearchUserLocationResult] = useState<boolean>(false);
    const [selectedPlaceId, updatedSelectedPlaceId] = useState<string>('');
    const [prevSelectedResult, setPrevSelectedResult] = useState<TGooglePlaceSuggestion | null>(null);

    const findCountry = (isoCode: string) => (countryList || []).find(x => x.IsoCode === isoCode);
 
    const getDealersFromCoords = async () => {
        const { Geocoder } = await google.maps.importLibrary("geocoding") as google.maps.GeocodingLibrary;
        const geocoder = new Geocoder();
        const point = new google.maps.LatLng(+(coords?.latitude), +(coords?.longitude));
        geocoder.geocode({ 'location': point }, (results, _) => {
            const address = results && results[0]?.address_components;
            if (address) {
                const countryDetail = address.find(x => x.types.includes('country'));
                if (countryDetail) {
                    const queryUrlParams = {
                        lat: coords?.latitude,
                        lng: coords?.longitude,
                        pageId: itemID,
                        language: language,
                        countryIsoCode: countryDetail.short_name,
                    };

                    const selectedCountry = findCountry(countryDetail.short_name);
                    if (!selectedCountry) {
                        updateCountry(undefined, true);
                        return;
                    }

                    // eslint-disable-next-line
                    const searchInput = (address || []).reduce((acc, iter) => acc + " " + iter.long_name, '')?.trim();
                    setCountry(selectedCountry);
                    setSearchInputValue(searchInput);
                    setShowError(false);

                    fetchDealersByGeoCode(queryUrlParams, controllerRef)
                        .then((data) => {
                            const dealershipResults = data?.dealershipResults;
                            onDealerResults({
                                results: dealershipResults || [],
                            }, true);
                        })
                        .catch((error) => {
                            onDealerResults({
                                results: []
                            });
                            console.error(error, 'Error while fetching Dealers');
                        });
                }
            }
        });
    }

    const setDealersCoords = (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude
        if (latitude && longitude) {
            setCoords({
                latitude,
                longitude
            });
        }
    }

    const showUserLocation = useCallback(() => {
        setUserLocationIsActive(true);
        windowService()?.navigator?.geolocation?.getCurrentPosition(setDealersCoords);
    }, []);

    const updateCountry = (country?: TCountry, showError = false) => {
        setCountry(country);
        setDealers([]);
        updateDealerResultsLoaded(false);
        setSearchInputValue('');
        setShowError(showError);
    }

    const onDealerResults = ({results}: onDealerResultsProps, userLocationSearchResultType = false): void => {
        const sortedResults = results.length ? sortDealersByDistance(results).slice(0, MAX_RESULTS) : results;
        setDealers(sortedResults);
        updateDealerResultsLoaded(true);
        setSearchUserLocationResult(userLocationSearchResultType);
    };

    const fetchData = useCallback(async () => {
        const getApiURL = `/${APIPrefix}/formdealerlocator/dealerlist/${itemID}/${language}`;
        try {
            const res = await fetch(getApiURL);
            if (!res.ok) {
                throw Error(`${res.status} : Failed to fetch data`);
            }
            const result = await res.json();
            if (result?.dealerSelectedCountries) {
                const countryList = result?.dealerSelectedCountries.map((x) => ({
                    Country: x.country,
                    IsoCode: x.countryisocode
                }));
                setCountryList(countryList);
                if (result?.dealerpreSelected) {
                    const deafultCountry = countryList?.find(x => x.IsoCode === result?.defaultDealerCountry)
                    setCountry(deafultCountry);
                }

                if ((result?.dealerRadius || '')?.includes('km')) {
                    updateDistanceUnit("km");
                } else {
                    updateDistanceUnit("mi");
                }
            } else {
                setCountryList([]);
            }
        } catch (error) {
            console.error(`${error} occurred while fetching data`);
        }
    }, []);

    useEffect(() => {
        if ((props.errors.length === 0) && !(props.value) && (country)) {
            updateCountry();
            fetchData();
        }
    }, [props.value])

    useEffect(() => {
        getDealersFromCoords();
    }, [coords])

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (props.value && countryList) {
          const { country, searchInputValue, latitude, longitude, searchUserLocationResult, selectedPlaceId } = JSON.parse(
            sessionStorageService().getItem(DEALER_LOCATOR_FORM_SEARCH) || '{}'
          );
          if (latitude && longitude && searchUserLocationResult) {
            setCoords({ latitude, longitude });
          } else if (country && searchInputValue) {
            setSearchInputValue(searchInputValue);
            setCountry(country);
            setPrevSelectedResult({ place_id: selectedPlaceId, description: searchInputValue ?? '' });
          }
        }
      }, [JSON.stringify(countryList)]);

    useEffect(() => {
        if (dealers.length) {
            const cardPropsInput: TCardProps[] = dealers.map(dealer => ({
                dealerName: dealer.dealership.dealerName,
                dealerNumber: dealer.dealership.cnhPrimarySAPNumber,
                shippingAddress: dealer.dealership.shippingAddress1,
                shippingCity: dealer.dealership.shippingCity,
                shippingZip: dealer.dealership.shippingZip,
                distance: dealer?.distance ?? '',
                shippingPhone: dealer.dealership?.shippingPhone ?? '',
                shippingStateProv:dealer.dealership?.shippingStateProv ?? '',
                distanceUnit
            }))
            updateCardProps(cardPropsInput);
        } else {
            updateCardProps([]);
        }
    }, [dealers]);

    const onDealerNumberUpdate = (dealerNumber: string) => {
        props.onChange(props.field.valueField.name, dealerNumber, props.isValid, []);
        sessionStorageService().setItem(
            DEALER_LOCATOR_FORM_SEARCH,
            JSON.stringify({ country, searchInputValue, searchUserLocationResult, ...coords, selectedPlaceId })
        );
    }


    useEffect(() => {
        const loader = new Loader({
            apiKey: mapsApiKey,
            version: "weekly",
        });
        loader.load().then(async () => {
            const { AutocompleteService, PlacesService } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;
            setAutocompleteService(new AutocompleteService());
            setPlacesService(new PlacesService(documentService().createElement('div')));
        });

        const formtag = dealerDivRef?.current?.closest('form');
        const submitButton = formtag?.querySelector("button[type='submit']");
        const reset = () => {
            if (!requiredField) {
                updateCountry();
                fetchData();
            }
        }
        if (submitButton) {
            submitButton.addEventListener('click', reset);
        }

        return () => {
            submitButton?.removeEventListener('click', reset);
        }

    }, []);

    return (
        <div className={'preferred-dealer'} ref={dealerDivRef}>
            <label className={'preferred-dealer__title'} htmlFor={id}>{label}</label>
            <div className='preferred-dealer__search-wrapper'>
                {/*  eslint-disable-next-line */}
                <div className={`preferred-dealer__user-position ${userLocationIsActive ? 'preferred-dealer__user-position--active' : '' }`} onClick={() => showUserLocation()}>
                    <span>
                        <DictionaryTerm keyword={'cnhi-formsDealerLocatorUserPosition'} />
                    </span>
                    <Icon
                        iconName={'geolocation-navigation-2'}
                        size={iconSize['mbl']}
                        color={userLocationIsActive ? "primary" : "grey"}
                    />
                </div>
                <span className='preferred-dealer__text-or'><DictionaryTerm keyword={'cnhi-formsDealerLocatorSearchOr'} /></span>
                {
                    autocompleteService && placesService && (
                        <DealerLocatorSearch
                            onDealerResults={onDealerResults}
                            country={country}
                            setCountry={updateCountry}
                            autocompleteService={autocompleteService}
                            placesService={placesService}
                            searchInputValue={searchInputValue}
                            setSearchInputValue={setSearchInputValue}
                            countries={countryList}
                            onTextInputClick={() => setUserLocationIsActive(false)}
                            updatedSelectedPlaceId={updatedSelectedPlaceId}
                            prevSelectedResult={prevSelectedResult}
                        />
                    )
                }
            </div>
            {showError && <div className='preferred-dealer__out-of-bound'><p><DictionaryTerm keyword={'cnhi-formsDealerLocatorCountryOutOfBound'} /></p></div>}
            {dealerResultsLoaded &&
                (<CardCarousel
                    value={props?.value}
                    updateSelectedDealerNumber={onDealerNumberUpdate}
                    cards={cardProps}
                    distanceUnit={distanceUnit}
                    desktopImage={formBgDesktopImage}
                    mobileImage={formBgMobileImage}
                />)
            }
            {FieldValidationErrors && <FieldValidationErrors {...props} />}
        </div>
    )
}

export default DealerLocator;